import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useDebounceValue } from '@sketch/utils'
import {
  Flex,
  Box,
  Text,
  LoadingPlaceholder,
  Checkbox,
  handleFetchMore,
  InfiniteList,
  Input,
  Tooltip,
} from '@sketch/components'
import {
  ListContainer,
  PageIcon,
  AllPrototypesIcon,
  PrototypeIcon,
  SearchContainer,
  SearchActions,
  SearchButton,
  Header,
  ErrorIcon,
  TryAgainButton,
} from './ContentsTab.styles'
import { dataIdFromObject } from '@sketch/graphql-cache'
import {
  useGetPagesQuery,
  useGetFramesForDocumentQuery,
} from '@sketch/gql-types'
import { usePreviewForm } from './usePreviewForm'
import { capitalize } from 'lodash'
import { PresentationManifest } from '@sketch-hq/sketch-web-renderer'
import {
  ContentsTabProps,
  ListItem,
  SelectedItem,
  FormattableEntry,
  ItemListProps,
} from './ContentsTab.types'
import {
  buildFrameMaps,
  traversePrototypeFlow,
  getPrototypePageUuids,
  fetchInitialManifest,
} from './ContentsTab.utils'

const ENTRIES_PATH = ['share', 'version', 'document', 'pages', 'entries']

const ItemList = ({
  handleSelectAll,
  items,
  after,
  fetchMore,
  toggleUUID,
  allChecked,
  selectedUUIDs,
  type,
  requiredPages,
  hasSearch,
}: ItemListProps) => {
  if (hasSearch && items.length === 0) {
    return <>No {type} found.</>
  }

  return (
    <>
      <Box mb={2}>
        <Checkbox
          name={`all${type}`}
          checked={allChecked}
          onChange={handleSelectAll}
        >
          <Flex alignItems="center">
            {type === 'pages' ? <PageIcon /> : <AllPrototypesIcon />}
            All {capitalize(type)}
          </Flex>
        </Checkbox>
      </Box>
      <ListContainer>
        <InfiniteList
          wrappingComponent={React.Fragment}
          renderLoading={() => <LoadingPlaceholder size="64px" />}
          canLoadMore={after !== null}
          onLoadMore={handleFetchMore(fetchMore, ENTRIES_PATH, {
            dataIdFromObject,
            after: after,
          })}
        >
          {items.map(({ uuid, name }) => {
            if (uuid === null) {
              return null
            }

            const isMandatoryPage =
              type === 'pages' && requiredPages.includes(uuid)

            if (isMandatoryPage) {
              return (
                <Box mb={2} key={uuid}>
                  <Tooltip content="This page is included with your prototype selection">
                    <Checkbox
                      name="selectedUUIDs"
                      checked={true}
                      disabled
                      onChange={() => toggleUUID(uuid, 'PAGE')}
                    >
                      <Flex alignItems="center">
                        <PageIcon />
                        {name}
                      </Flex>
                    </Checkbox>
                  </Tooltip>
                </Box>
              )
            }

            return (
              <Box mb={2} key={uuid}>
                <Checkbox
                  name="selectedUUIDs"
                  checked={selectedUUIDs.some(item => item.uuid === uuid)}
                  onChange={() =>
                    toggleUUID(uuid, type === 'pages' ? 'PAGE' : 'PROTOTYPE')
                  }
                >
                  <Flex alignItems="center">
                    {type === 'pages' ? <PageIcon /> : <PrototypeIcon />}
                    {name}
                  </Flex>
                </Checkbox>
              </Box>
            )
          })}
        </InfiniteList>
      </ListContainer>
    </>
  )
}

/**
 * Compares two selectedUUIDs arrays for equality, ignoring order.
 * Returns true if both arrays contain the same items (by uuid).
 */
const areSelectedUUIDsEqual = (
  a: SelectedItem[],
  b: SelectedItem[]
): boolean => {
  if (a.length !== b.length) return false
  const aUuids = new Set(a.map(item => item.uuid))
  return b.every(item => aUuids.has(item.uuid))
}

export const ContentsTab = ({
  shareIdentifier,
  versionShortId,
}: ContentsTabProps) => {
  const { values, setFieldValue, initialValues } = usePreviewForm()
  const selectedUUIDs: SelectedItem[] = useMemo(
    () => values.selectedUUIDs || [],
    [values.selectedUUIDs]
  )

  /**
   * Smart setter for selectedUUIDs that restores the original initialValues reference
   * when the new selection matches the initial state. This ensures Formik's shallow
   * equality check correctly detects when the form returns to its initial state.
   */
  const setSelectedUUIDs = React.useCallback(
    (newSelectedUUIDs: SelectedItem[]) => {
      const initialSelectedUUIDs = initialValues.selectedUUIDs || []
      if (areSelectedUUIDsEqual(newSelectedUUIDs, initialSelectedUUIDs)) {
        setFieldValue('selectedUUIDs', initialSelectedUUIDs)
      } else {
        setFieldValue('selectedUUIDs', newSelectedUUIDs)
      }
    },
    [setFieldValue, initialValues.selectedUUIDs]
  )
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounceValue(searchTerm, 300)
  const [manifest, setManifest] = useState<PresentationManifest | null>(null)

  const { data, loading, error, fetchMore, refetch } = useGetPagesQuery({
    variables: {
      shareIdentifier,
      versionShortId,
      search: debouncedSearchTerm,
    },
  })

  const prototypeFramesQuery = useGetFramesForDocumentQuery({
    variables: {
      identifier: shareIdentifier,
      versionShortId,
      search: debouncedSearchTerm,
      filter: 'PROTOTYPE',
    },
  })

  const afterPages = data?.share?.version?.document?.pages?.meta.after || null
  const afterPrototypes =
    prototypeFramesQuery.data?.share?.version?.document?.frames.meta.after ||
    null

  const formatEntry = (entries: FormattableEntry[]) =>
    entries
      .map(entry => ({
        name: entry.name,
        uuid: entry.uuid,
      }))
      .filter(
        (page): page is ListItem => page.name !== null && page.uuid !== null
      )

  const pages = useMemo(
    () => formatEntry(data?.share?.version?.document?.pages?.entries || []),
    [data?.share?.version?.document?.pages?.entries]
  )

  const prototypes = useMemo(
    () =>
      formatEntry(
        prototypeFramesQuery.data?.share?.version?.document?.frames.entries ||
          []
      ),
    [prototypeFramesQuery.data?.share?.version?.document?.frames.entries]
  )

  const allPageIds = useMemo(() => pages.map(entry => entry.uuid), [pages])

  const hasCleanedInvalidUUIDs = useRef(false)

  useEffect(() => {
    // Only run once when pages and prototypes have loaded
    if (
      (hasCleanedInvalidUUIDs.current ||
        loading ||
        prototypeFramesQuery.loading ||
        pages.length === 0) &&
      afterPages === null &&
      afterPrototypes === null
    ) {
      return
    }

    // Check if the selected UUIDs (when coming from edit)
    // are present in the page/prototype ids from the queries
    // aka if a page or prototype has been deleted and the preview is
    // in an invalid state
    const validUUIDs = initialValues.selectedUUIDs.filter(selectedUUID =>
      selectedUUID.type === 'PROTOTYPE'
        ? prototypes.some(proto => proto.uuid === selectedUUID.uuid)
        : pages.some(page => page.uuid === selectedUUID.uuid)
    )

    // Only update if there were invalid UUIDs
    if (validUUIDs.length !== initialValues.selectedUUIDs.length) {
      hasCleanedInvalidUUIDs.current = true
      setSelectedUUIDs(validUUIDs)
    }
  }, [
    pages,
    prototypes,
    loading,
    prototypeFramesQuery.loading,
    initialValues.selectedUUIDs,
    setSelectedUUIDs,
    afterPages,
    afterPrototypes,
  ])

  const allPrototypeIds = useMemo(
    () => prototypes.map(entry => entry.uuid),
    [prototypes]
  )

  const fullPrototypes = useMemo(
    () =>
      prototypeFramesQuery.data?.share?.version?.document?.frames.entries || [],
    [prototypeFramesQuery.data]
  )

  // We'll use the length of prototypes to determine if we need to load the manifest
  // this will avoid a flash of loading in cases where there are no prototypes
  const [isLoadingManifest, setIsLoadingManifest] = useState(
    fullPrototypes.length > 0
  )

  // Fetch manifest once on component mount
  useEffect(() => {
    fetchInitialManifest(fullPrototypes).then(manifestData => {
      setManifest(manifestData)
      setIsLoadingManifest(false)
    })
  }, [fullPrototypes])

  // Calculate required pages based on selected prototypes
  const requiredPages = useMemo(() => {
    const selectedPrototypeIds = selectedUUIDs
      .filter(item => item.type === 'PROTOTYPE')
      .map(item => item.uuid)

    if (selectedPrototypeIds.length === 0 || !manifest) {
      return []
    }

    // Calculate synchronously using cached manifest
    const allPageUuidsArrays = selectedPrototypeIds.map(id => {
      const prototype = fullPrototypes.find(p => p.uuid === id)
      const fallbackPageUuid = prototype?.page?.uuid

      if (!manifest?.contents?.pages) {
        return fallbackPageUuid ? [fallbackPageUuid] : []
      }

      const { frameToPageMap, frameDataMap } = buildFrameMaps(manifest)
      const pageUuids = traversePrototypeFlow(id, frameToPageMap, frameDataMap)

      return pageUuids.length > 0
        ? pageUuids
        : fallbackPageUuid
          ? [fallbackPageUuid]
          : []
    })

    return [...new Set(allPageUuidsArrays.flat())]
  }, [selectedUUIDs, fullPrototypes, manifest])

  const handleAddUUID = (id: string, type: 'PAGE' | 'PROTOTYPE') => {
    if (!selectedUUIDs.some(item => item.uuid === id)) {
      setSelectedUUIDs([...selectedUUIDs, { uuid: id, type }])
    }
  }

  const handleRemoveUUID = (id: string) => {
    setSelectedUUIDs(selectedUUIDs.filter(item => item.uuid !== id))
  }

  /**
   * Selects a prototype and all of its required pages.
   */
  const selectPrototypeWithPages = async (
    prototypeId: string,
    currentSelected: SelectedItem[]
  ) => {
    const pageUuids = await getPrototypePageUuids(
      prototypeId,
      fullPrototypes,
      manifest
    )
    const itemsToAdd: SelectedItem[] = [
      { uuid: prototypeId, type: 'PROTOTYPE' },
    ]

    // Add all required pages that aren't already selected
    pageUuids.forEach(pageUuid => {
      if (!currentSelected.some(item => item.uuid === pageUuid)) {
        itemsToAdd.push({ uuid: pageUuid, type: 'PAGE' })
      }
    })

    setSelectedUUIDs([...currentSelected, ...itemsToAdd])
  }

  const toggleUUID = (id: string, type: 'PAGE' | 'PROTOTYPE') => {
    const currentSelected = values.selectedUUIDs || []

    if (currentSelected.some((item: SelectedItem) => item.uuid === id)) {
      handleRemoveUUID(id)
    } else {
      if (type === 'PROTOTYPE') {
        selectPrototypeWithPages(id, currentSelected)
        return
      }

      handleAddUUID(id, type)
    }
  }

  const handleSelectAllPages = () => {
    const areAllFilteredPagesSelected = allPageIds.every(id =>
      selectedUUIDs.some(item => item.uuid === id)
    )
    if (areAllFilteredPagesSelected) {
      // Deselect filtered pages
      setSelectedUUIDs(
        selectedUUIDs.filter(item => !allPageIds.includes(item.uuid))
      )
    } else {
      // Select all filtered pages (keep existing prototypes and other pages)
      const newPageItems = allPageIds
        .filter(id => !selectedUUIDs.some(item => item.uuid === id))
        .map(id => ({ uuid: id, type: 'PAGE' as const }))
      setSelectedUUIDs([...selectedUUIDs, ...newPageItems])
    }
  }

  /**
   * Deselects all prototypes and their associated required pages.
   */
  const deselectAllPrototypesWithPages = async () => {
    const allPageUuidsArrays = await Promise.all(
      allPrototypeIds.map((id: string) =>
        getPrototypePageUuids(id, fullPrototypes, manifest)
      )
    )
    const prototypePagesUUIDs = [...new Set(allPageUuidsArrays.flat())]

    setSelectedUUIDs(
      selectedUUIDs.filter(
        item =>
          !allPrototypeIds.includes(item.uuid) &&
          !prototypePagesUUIDs.includes(item.uuid)
      )
    )
  }

  /**
   * Selects all unselected prototypes and their required pages.
   */
  const selectAllPrototypesWithPages = async () => {
    const currentSelected = values.selectedUUIDs || []
    const unselectedPrototypeIds = allPrototypeIds.filter(
      id => !currentSelected.some((item: SelectedItem) => item.uuid === id)
    )

    const results = await Promise.all(
      unselectedPrototypeIds.map(async prototypeId => ({
        prototypeId,
        pageUuids: await getPrototypePageUuids(
          prototypeId,
          fullPrototypes,
          manifest
        ),
      }))
    )

    const itemsToAdd: SelectedItem[] = []
    const pagesToAdd = new Set<string>()

    results.forEach(({ prototypeId, pageUuids }) => {
      itemsToAdd.push({ uuid: prototypeId, type: 'PROTOTYPE' })

      pageUuids.forEach(pageUuid => {
        if (
          !currentSelected.some(
            (item: SelectedItem) => item.uuid === pageUuid
          ) &&
          !pagesToAdd.has(pageUuid)
        ) {
          pagesToAdd.add(pageUuid)
          itemsToAdd.push({ uuid: pageUuid, type: 'PAGE' })
        }
      })
    })

    setSelectedUUIDs([...currentSelected, ...itemsToAdd])
  }

  const handleSelectAllPrototypes = () => {
    const areAllPrototypesSelected = allPrototypeIds.every(id =>
      selectedUUIDs.some(item => item.uuid === id)
    )

    if (areAllPrototypesSelected) {
      deselectAllPrototypesWithPages()
    } else {
      selectAllPrototypesWithPages()
    }
  }

  const handleGlobalSelectAll = () => {
    const allItems = [
      ...allPageIds.map(id => ({ uuid: id, type: 'PAGE' as const })),
      ...allPrototypeIds.map(id => ({
        uuid: id,
        type: 'PROTOTYPE' as const,
      })),
    ]
    setSelectedUUIDs(allItems)
  }

  const handleGlobalDeselectAll = () => {
    setSelectedUUIDs([])
  }

  const isLoading = !!(loading || prototypeFramesQuery.loading)

  if (error || prototypeFramesQuery.error) {
    return (
      <>
        <Header>
          Select the prototypes or pages that will be available in this preview.
        </Header>
        <Flex
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          height={500}
        >
          <ErrorIcon />
          <Text pt={4} pb={4} textAlign="center" fontSize="F">
            Failed to load preview settings
          </Text>
          <Flex>
            <TryAgainButton variant="primary" onClick={() => refetch()}>
              Try Again
            </TryAgainButton>
          </Flex>
        </Flex>
      </>
    )
  }

  return (
    <>
      <Header>
        Select the prototypes or pages that will be available in this preview.
      </Header>

      <SearchContainer>
        <Input
          type="text"
          placeholder="Search for specific prototypes or pages"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <SearchActions>
          <SearchButton
            variant="primary"
            disabled={
              isLoading ||
              selectedUUIDs.length ===
                allPageIds.length + allPrototypeIds.length
            }
            onClick={handleGlobalSelectAll}
          >
            Select All
          </SearchButton>
          <SearchButton
            disabled={isLoading || selectedUUIDs.length === 0}
            variant="primary"
            onClick={handleGlobalDeselectAll}
          >
            Deselect All
          </SearchButton>
        </SearchActions>
      </SearchContainer>

      {isLoading || isLoadingManifest ? (
        <Flex justifyContent="center" height={384}>
          <LoadingPlaceholder />
        </Flex>
      ) : (
        <>
          {prototypes.length > 0 && (
            <Box mt={5}>
              <ItemList
                hasSearch={searchTerm !== ''}
                type="prototypes"
                handleSelectAll={handleSelectAllPrototypes}
                toggleUUID={toggleUUID}
                fetchMore={fetchMore}
                items={prototypes}
                selectedUUIDs={selectedUUIDs}
                after={afterPrototypes}
                allChecked={allPrototypeIds.every(id =>
                  selectedUUIDs.some(item => item.uuid === id)
                )}
                requiredPages={requiredPages}
              />
            </Box>
          )}
          <Box mt={5}>
            <ItemList
              hasSearch={searchTerm !== ''}
              type="pages"
              handleSelectAll={handleSelectAllPages}
              toggleUUID={toggleUUID}
              fetchMore={fetchMore}
              items={pages}
              selectedUUIDs={selectedUUIDs}
              after={afterPages}
              allChecked={allPageIds.every(id =>
                selectedUUIDs.some(item => item.uuid === id)
              )}
              requiredPages={requiredPages}
            />
          </Box>
        </>
      )}
    </>
  )
}
